/**
 * Canonical Survey Verification Panel
 * ====================================
 * Groups survey occurrences into canonical legal entities.
 * 
 * Legal Principle:
 * - 67 OCR occurrences ≠ 67 legal verifications
 * - Advocate verifies UNIQUE survey parcels, not every mention
 * - One verification propagates to all occurrences
 * 
 * UI: Collapsed accordion showing canonical surveys with occurrence counts
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronRight,
  Eye, Edit3, Search, Filter, Maximize2, Minimize2, X, RefreshCw,
  FileText, MapPin, Clock, User, Info, CheckSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status badge colors
const STATUS_COLORS = {
  VERIFIED: 'bg-green-100 text-green-800 border-green-200',
  PENDING: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  CONFLICT: 'bg-red-100 text-red-800 border-red-200',
  REJECTED: 'bg-gray-100 text-gray-800 border-gray-200'
};

const STATUS_ICONS = {
  VERIFIED: CheckCircle,
  PENDING: Clock,
  CONFLICT: AlertTriangle,
  REJECTED: XCircle
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CanonicalSurveyPanel = ({ caseId, isOpen, onClose }) => {
  const [canonicalSurveys, setCanonicalSurveys] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSurveys, setExpandedSurveys] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Edit state
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch canonical surveys
  const fetchCanonicalSurveys = useCallback(async () => {
    if (!caseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_BASE}/api/survey-verification/case/${caseId}/canonical`
      );
      
      setCanonicalSurveys(response.data.canonical_surveys || []);
      setSummary(response.data.summary || null);
      
    } catch (err) {
      console.error('Failed to fetch canonical surveys:', err);
      setError('Failed to load survey data');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    if (isOpen && caseId) {
      fetchCanonicalSurveys();
    }
  }, [isOpen, caseId, fetchCanonicalSurveys]);

  // Toggle survey expansion
  const toggleExpanded = (canonicalId) => {
    setExpandedSurveys(prev => {
      const next = new Set(prev);
      if (next.has(canonicalId)) {
        next.delete(canonicalId);
      } else {
        next.add(canonicalId);
      }
      return next;
    });
  };

  // Verify a canonical survey
  const handleVerify = async (survey, correctedValue = null) => {
    setSaving(true);
    setError(null);
    
    try {
      const verifiedValue = correctedValue || survey.canonical_value;
      
      const response = await axios.post(
        `${API_BASE}/api/survey-verification/canonical/${survey.canonical_id}/verify`,
        {
          canonical_id: survey.canonical_id,
          case_id: caseId,  // Pass case_id to scope verification
          verified_value: verifiedValue,
          user_id: 'current_user',
          notes: editNotes || 'Verified via canonical panel',
          sync_documents: true
        }
      );
      
      console.log('✅ Verification result:', response.data);
      
      // Refresh data
      await fetchCanonicalSurveys();
      setEditingSurvey(null);
      setEditValue('');
      setEditNotes('');
      
    } catch (err) {
      console.error('Verification failed:', err);
      setError(`Failed to verify survey: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // State for individual occurrence edits
  const [occurrenceEdits, setOccurrenceEdits] = useState({});

  // Start editing - initialize edits for each occurrence
  const startEdit = (survey) => {
    setEditingSurvey(survey);
    setEditValue(survey.verified_value || survey.canonical_value);
    setEditNotes('');
    
    // Initialize individual edits from occurrences
    const initialEdits = {};
    survey.occurrences.forEach(occ => {
      initialEdits[occ.anchor_id] = occ.raw_value;
    });
    setOccurrenceEdits(initialEdits);
  };

  // Update individual occurrence value
  const updateOccurrenceEdit = (anchorId, value) => {
    setOccurrenceEdits(prev => ({
      ...prev,
      [anchorId]: value
    }));
  };

  // Apply value to all occurrences
  const applyToAll = (value) => {
    const newEdits = {};
    Object.keys(occurrenceEdits).forEach(key => {
      newEdits[key] = value;
    });
    setOccurrenceEdits(newEdits);
    setEditValue(value);
  };

  // Filter surveys
  const filteredSurveys = canonicalSurveys.filter(survey => {
    // Status filter
    if (statusFilter !== 'ALL' && survey.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        survey.canonical_value.toLowerCase().includes(search) ||
        survey.base_number.includes(search) ||
        survey.variants.some(v => v.toLowerCase().includes(search))
      );
    }
    
    return true;
  });

  if (!isOpen) return null;

  // Panel container classes
  const panelClasses = isFullscreen
    ? 'fixed inset-0 z-50 bg-white'
    : 'fixed right-0 top-0 h-full w-[600px] bg-white shadow-2xl z-50 border-l';

  return (
    <div className={panelClasses}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-blue-600" />
            Survey Verification
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Canonical grouping: Verify once, update everywhere
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsFullscreen(!isFullscreen)}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchCanonicalSurveys} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="p-3 bg-gray-50 border-b">
          <div className="grid grid-cols-5 gap-2 text-center">
            <div className="bg-white rounded p-2 border">
              <div className="text-lg font-bold text-gray-900">{summary.total_canonical_surveys}</div>
              <div className="text-xs text-gray-500">Surveys</div>
            </div>
            <div className="bg-white rounded p-2 border">
              <div className="text-lg font-bold text-green-600">{summary.verified}</div>
              <div className="text-xs text-gray-500">Verified</div>
            </div>
            <div className="bg-white rounded p-2 border">
              <div className="text-lg font-bold text-yellow-600">{summary.pending}</div>
              <div className="text-xs text-gray-500">Pending</div>
            </div>
            <div className="bg-white rounded p-2 border">
              <div className="text-lg font-bold text-red-600">{summary.conflicts}</div>
              <div className="text-xs text-gray-500">Conflicts</div>
            </div>
            <div className="bg-white rounded p-2 border">
              <div className="text-lg font-bold text-blue-600">{summary.total_occurrences}</div>
              <div className="text-xs text-gray-500">Occurrences</div>
            </div>
          </div>
          {summary.verification_reduction && (
            <div className="mt-2 text-xs text-center text-gray-600 bg-blue-50 rounded p-1">
              📊 {summary.verification_reduction}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="p-3 border-b flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search surveys..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 text-sm border rounded px-2"
        >
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="VERIFIED">Verified</option>
          <option value="CONFLICT">Conflicts</option>
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border-b text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Survey List */}
      <div className="flex-1 overflow-y-auto" style={{ height: 'calc(100% - 240px)' }}>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : filteredSurveys.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No surveys found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredSurveys.map((survey) => (
              <CanonicalSurveyRow
                key={survey.canonical_id}
                survey={survey}
                isExpanded={expandedSurveys.has(survey.canonical_id)}
                onToggle={() => toggleExpanded(survey.canonical_id)}
                onVerify={() => handleVerify(survey)}
                onEdit={() => startEdit(survey)}
                saving={saving}
              />
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal - Shows all occurrences for editing */}
      {editingSurvey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
              <h3 className="font-semibold text-lg">
                Edit Survey {editingSurvey.base_number}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {editingSurvey.occurrence_count} occurrences found • Edit each or apply one value to all
              </p>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Apply to All Section */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <label className="block text-sm font-semibold text-green-800 mb-2">
                  Quick: Set same value for all
                </label>
                <div className="flex gap-2">
                  <Input
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="e.g., 480/A"
                    className="flex-1 font-mono"
                  />
                  <Button
                    onClick={() => applyToAll(editValue)}
                    disabled={!editValue.trim()}
                    variant="outline"
                    className="whitespace-nowrap"
                  >
                    Apply to All
                  </Button>
                </div>
              </div>
              
              {/* Individual Occurrences - Editable */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Edit Individual Values ({editingSurvey.occurrences.length})
                </label>
                <div className="border rounded-lg divide-y max-h-[350px] overflow-y-auto">
                  {editingSurvey.occurrences.map((occ, idx) => (
                    <div 
                      key={occ.anchor_id || idx}
                      className="flex items-center gap-3 p-3 hover:bg-gray-50"
                    >
                      {/* Original Value (read-only reference) */}
                      <div className="w-24 flex-shrink-0">
                        <span className="text-xs text-gray-400 block">Original:</span>
                        <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                          {occ.raw_value}
                        </span>
                      </div>
                      
                      {/* Arrow */}
                      <span className="text-gray-400">→</span>
                      
                      {/* Editable Input */}
                      <div className="flex-1">
                        <Input
                          value={occurrenceEdits[occ.anchor_id] || occ.raw_value}
                          onChange={(e) => updateOccurrenceEdit(occ.anchor_id, e.target.value)}
                          className="font-mono text-sm h-8"
                          placeholder="Corrected value"
                        />
                      </div>
                      
                      {/* Document Info */}
                      <div className="w-32 flex-shrink-0 text-right">
                        <span className="text-xs text-gray-500 truncate block">
                          {occ.document_name || 'Document'}
                        </span>
                      </div>
                      
                      {/* Status */}
                      {occ.status === 'VERIFIED' && (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Notes (optional)
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this verification..."
                  className="w-full border rounded p-2 text-sm h-16"
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {Object.keys(occurrenceEdits).length} values will be saved
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setEditingSurvey(null);
                  setOccurrenceEdits({});
                }}>
                  Cancel
                </Button>
                <Button
                  onClick={() => handleVerify(editingSurvey, editValue)}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Save & Verify All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CANONICAL SURVEY ROW
// ============================================================================

const CanonicalSurveyRow = ({ survey, isExpanded, onToggle, onVerify, onEdit, saving }) => {
  const StatusIcon = STATUS_ICONS[survey.status] || Clock;
  const statusColor = STATUS_COLORS[survey.status] || STATUS_COLORS.PENDING;
  
  return (
    <div className="border-b last:border-b-0">
      {/* Main Row */}
      <div 
        className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
          survey.has_conflict ? 'bg-red-50/30' : ''
        }`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {/* Expand/Collapse */}
          <button className="text-gray-400 hover:text-gray-600">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          
          {/* Survey Number */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono font-semibold text-gray-900">
                {/* Show verified_value if corrected, otherwise canonical_value */}
                {survey.verified_value && survey.verified_value !== survey.canonical_value ? (
                  <>
                    <span className="line-through text-gray-400 mr-1">{survey.canonical_value}</span>
                    <span className="text-green-700">{survey.verified_value}</span>
                  </>
                ) : (
                  survey.canonical_value
                )}
              </span>
              <Badge variant="outline" className={`text-xs ${statusColor}`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {survey.status}
              </Badge>
              {survey.has_conflict && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Conflict
                </Badge>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {survey.occurrence_count} occurrence{survey.occurrence_count !== 1 ? 's' : ''} • 
              Base: {survey.base_number}
              {survey.subdivision && ` • Subdiv: ${survey.subdivision}`}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="ghost"
              onClick={onEdit}
              title="Edit & Verify"
            >
              <Edit3 className="w-4 h-4" />
            </Button>
            {survey.status !== 'VERIFIED' && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onVerify}
                disabled={saving}
                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                title="Quick Verify"
              >
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-3 pb-3 bg-gray-50/50">
          {/* Variants */}
          <div className="mb-2">
            <div className="text-xs font-medium text-gray-500 mb-1">Detected Variants:</div>
            <div className="flex flex-wrap gap-1">
              {survey.variants.map((variant, idx) => (
                <span 
                  key={idx}
                  className="px-2 py-0.5 bg-white border rounded text-xs font-mono"
                >
                  {variant}
                </span>
              ))}
            </div>
          </div>
          
          {/* Conflict Info */}
          {survey.has_conflict && survey.conflict_reason && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              {survey.conflict_reason}
            </div>
          )}
          
          {/* Occurrences */}
          <div>
            <div className="text-xs font-medium text-gray-500 mb-1">Occurrences:</div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {survey.occurrences.slice(0, 10).map((occ, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 text-xs bg-white p-1.5 rounded border"
                >
                  <FileText className="w-3 h-3 text-gray-400" />
                  <span className="font-mono">{occ.raw_value}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500 truncate flex-1">{occ.document_name}</span>
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] ${
                      occ.status === 'VERIFIED' ? 'text-green-600' : 'text-yellow-600'
                    }`}
                  >
                    {occ.status === 'VERIFIED' ? '✓' : '○'}
                  </Badge>
                </div>
              ))}
              {survey.occurrences.length > 10 && (
                <div className="text-xs text-gray-400 text-center py-1">
                  +{survey.occurrences.length - 10} more occurrences
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanonicalSurveyPanel;
